import React, {
    useState,
    useMemo,
    useRef,
    useEffect
} from "react";

import {
    Box,
    TextField,
    Button,
    IconButton,
    Typography,
    Paper,
    Grid,
    Alert,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TableHead
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AddIcon from "@mui/icons-material/Add";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ClearIcon from "@mui/icons-material/Clear";
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import GridOnIcon from "@mui/icons-material/GridOn";
import {
    useLocation
} from "react-router-dom";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import MicroTipApi from "../../../api/MicroTipApi";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ConfirmDialog from "../../Common/ConfirmDialog";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import html2canvas from "html2canvas";
const Predict = () => {

    // =========================
    // DEFAULT ROWS
    // =========================
    const createEmptyRows = (count = 5) => {

        return Array.from({ length: count }, () => ({
            sampleId: "",
            response: "",
            concentration: ""
        }));
    };
    const [openValidationDialog, setOpenValidationDialog] = useState(false);
    const [validationMessage, setValidationMessage] = useState("");
    const [rows, setRows] = useState(createEmptyRows(5));
    const [openResetDialog, setOpenResetDialog] =
        useState(false);

    const [pendingRefresh, setPendingRefresh] =
        useState(false);
    const [inputValue, setInputValue] = useState("");

    const [error, setError] = useState("");

    const fileInputRef = useRef(null);
    const chartRef = useRef(null);

    const location = useLocation();

    // =========================
    // GET RUN ID
    // =========================
    const runId = useMemo(() => {

        // FROM ROUTER STATE
        const stateRunId = location?.state?.runId;

        if (stateRunId) {

            // SAVE LATEST RUN ID
            sessionStorage.setItem(
                "selectedRunId",
                stateRunId
            );

            return stateRunId;
        }

        // AFTER REFRESH
        return sessionStorage.getItem(
            "selectedRunId"
        );

    }, [location]);


    // =========================
    // SELECTED RUN DATA
    // =========================
    const rawData = useMemo(() => {

        if (!runId) {

            return {};
        }

        try {

            const latestHistory = JSON.parse(
                sessionStorage.getItem(
                    "calibrationHistory"
                ) || "{}"
            );

            return latestHistory?.[runId] || {};

        } catch (err) {

            console.error(
                "Failed to load selected run",
                err
            );

            return {};
        }

    }, [runId]);


    const calibrationResponse =
        rawData?.result || rawData?.resultData || {};

    const description =
        rawData?.description || "";

    const result =
        rawData?.result || rawData?.resultData || {};

    const calData =
        rawData?.rows || [];

    const splines =
        rawData?.splines || [];


    // =========================
    // API
    // =========================
    const {
        callPredictApi,
        predictLoading,
        predictError
    } = MicroTipApi(
        {},
        [],
        splines,
        calibrationResponse,
        0
    );

    // =========================
    // PARSE INPUT
    // =========================
    const parseInput = (text) => {

        try {

            setError("");

            if (!text || !text.trim()) {

                setRows(createEmptyRows(5));

                return;
            }

            const lines = text.split(/\r?\n/);

            const parsed = [];

            lines.forEach((line, index) => {

                if (!line.trim()) return;

                let parts = line.split("\t");

                if (parts.length < 2) {
                    parts = line.trim().split(/\s+/);
                }

                // skip header
                if (
                    parts[0]?.toLowerCase().includes("sample") ||
                    parts[1]?.toLowerCase().includes("response")
                ) {
                    return;
                }

                let sampleId = parts[0]?.trim();

                let response = parts[1]?.trim();

                // only response
                if (parts.length === 1) {

                    sampleId = "";

                    response = parts[0];
                }

                if (sampleId === "" && parts.length >= 1) {

                    response = parts[parts.length - 1];
                }

                if (
                    response === undefined ||
                    response === ""
                ) {
                    throw new Error(
                        `Missing response at line ${index + 1}`
                    );
                }

                if (isNaN(Number(response))) {

                    throw new Error(
                        `Invalid response at line ${index + 1}`
                    );
                }

                parsed.push({
                    sampleId: sampleId || "",
                    response: response,
                    concentration: ""
                });

            });

            setRows(
                parsed.length
                    ? parsed
                    : createEmptyRows(5)
            );

        } catch (err) {

            setError(err.message);

            setRows(createEmptyRows(5));
        }
    };

    // =========================
    // TEXT INPUT
    // =========================
    const handleBulkInput = (e) => {

        setInputValue(e.target.value);

        parseInput(e.target.value);
    };


    // =========================
    // FILE READ
    // =========================
    const handleFileRead = (file) => {

        if (!file) return;

        const fileName = file.name.toLowerCase();

        // =========================
        // EXCEL FILE
        // =========================
        if (
            fileName.endsWith(".xlsx") ||
            fileName.endsWith(".xls")
        ) {

            const reader = new FileReader();

            reader.onload = (e) => {

                try {

                    const data = new Uint8Array(
                        e.target.result
                    );

                    const workbook = XLSX.read(data, {
                        type: "array"
                    });

                    // FIRST SHEET
                    const sheetName =
                        workbook.SheetNames[0];

                    const worksheet =
                        workbook.Sheets[sheetName];

                    // ARRAY FORMAT
                    const jsonData =
                        XLSX.utils.sheet_to_json(
                            worksheet,
                            {
                                header: 1,
                                defval: ""
                            }
                        );

                    // CONVERT TO TAB TEXT
                    const COLUMN_WIDTH = 20;

                    const formattedText = jsonData
                        .map((row) =>
                            row
                                .map((cell) =>
                                    String(cell ?? "")
                                        .replace(/\t/g, " ")
                                        .trim()
                                        .padEnd(COLUMN_WIDTH, " ")
                                )
                                .join("")
                        )
                        .join("\n");

                    setInputValue(formattedText);

                    parseInput(formattedText);

                } catch (err) {

                    console.error(err);

                    setError(
                        "Failed to read Excel file."
                    );
                }
            };

            reader.readAsArrayBuffer(file);

            return;
        }

        // =========================
        // TXT / CSV
        // =========================
        const reader = new FileReader();

        reader.onload = (event) => {

            const text = event.target.result;

            setInputValue(text);

            parseInput(text);
        };

        reader.readAsText(file);
    };

    // =========================
    // FILE UPLOAD
    // =========================
    const handleFileUpload = (e) => {

        const file = e.target.files?.[0];

        if (file) {
            handleFileRead(file);
        }

        // allow selecting same file again
        e.target.value = null;
    };

    // =========================
    // DRAG DROP
    // =========================
    const handleDrop = (e) => {

        e.preventDefault();

        const file = e.dataTransfer.files?.[0];

        handleFileRead(file);
    };

    // =========================
    // ROW UPDATE
    // =========================
    const handleRowChange = (
        index,
        field,
        value
    ) => {

        const updated = [...rows];

        updated[index][field] = value;

        setRows(updated);
    };

    // =========================
    // PREDICT
    // =========================
    const hasEmptyCalibration = (data) => {
        return data?.some(
            (row) =>
                row.concentration === "" ||
                row.concentration === null ||
                row.concentration === undefined ||
                row.response === "" ||
                row.response === null ||
                row.response === undefined
        );
    };
    const handlePredict = async () => {

        try {

            const validRows = rows.filter(
                (r) =>
                    r.response !== undefined &&
                    r.response !== null &&
                    String(r.response).trim() !== ""
            );

            if (validRows.length === 0) {

                setError(
                    "Please enter at least one response value."
                );

                return;
            }

            setError("");

            const result =
                await callPredictApi(validRows);

            if (result) {
                if (hasEmptyCalibration(result)) {
                    setValidationMessage(
                        "Predictions contains empty Response values or invalid Calibration data. Please verify before proceeding."
                    );
                    setOpenValidationDialog(true);
                    return;
                }
                setRows(result);

                setShowChart(true);

                // Update session storage with latest prediction rows
                const existing = JSON.parse(
                    sessionStorage.getItem("calibrationHistory") || "{}"
                );

                const updated = {
                    ...existing,
                    [runId]: {
                        ...rawData,
                        predictionRows: result
                    }
                };

                sessionStorage.setItem(
                    "calibrationHistory",
                    JSON.stringify(updated)
                );
            }

        } catch (err) {

            console.error(err);

            setError(
                "Prediction failed. Please try again."
            );
        }
    };

    // =========================
    // CLEAR
    // =========================
    const handleClear = () => {

        setRows(createEmptyRows(5));
        setShowChart(false);

        setError("");

        setInputValue("");
    };

    // =========================
    // ADD ROW
    // =========================
    const handleAddRow = () => {

        setRows([
            ...rows,
            {
                sampleId: "",
                response: "",
                concentration: ""
            }
        ]);
    };

    // =========================
    // DELETE ROW
    // =========================
    const handleDeleteRow = (index) => {

        const updated =
            rows.filter((_, i) => i !== index);

        setRows(
            updated.length
                ? updated
                : createEmptyRows(1)
        );
    };

    // =========================
    // RESET
    // =========================
    const handleResetRows = () => {
        setShowChart(false);
        setRows(createEmptyRows(5));
        const existing = JSON.parse(
            sessionStorage.getItem("calibrationHistory") || "{}"
        );

        const updated = {
            ...existing,
            [runId]: {
                ...existing[runId],
                predictionRows: createEmptyRows(5)   
            }
        };

        sessionStorage.setItem(
            "calibrationHistory",
            JSON.stringify(updated)
        );
    };


    // ========================================
    // EXCEL EXPORT
    // ========================================
    const handleExportExcel = async () => {

        const workbook = new ExcelJS.Workbook();

        workbook.creator = "LIIMS Portal";
        workbook.created = new Date();

        const sheet = workbook.addWorksheet(
            "Prediction Report",
            {
                views: [
                    {
                        state: "frozen",
                        ySplit: 1
                    }
                ]
            }
        );

        // ========================================
        // DEFAULT STYLES
        // ========================================

        const headerFill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "774DEF" }
        };

        const headerFont = {
            bold: true,
            color: { argb: "FFFFFF" },
            size: 11
        };

        const sectionFont = {
            bold: true,
            size: 12
        };

        const borderStyle = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" }
        };

        const centerAlignment = {
            vertical: "middle",
            horizontal: "center"
        };

        let rowIndex = 1;

        // ========================================
        // LOGO + HEADER BRANDING
        // ========================================

        // Optional Logo
        // Replace with your base64 logo if needed

        /*
        const logoId = workbook.addImage({
            base64: logoBase64,
            extension: "png"
        });
    
        sheet.addImage(logoId, {
            tl: { col: 0, row: 0 },
            ext: { width: 120, height: 50 }
        });
        */

        sheet.mergeCells("A1:D1");

        const titleCell = sheet.getCell("A1");

        titleCell.value = "LIMS PORTAL VALIDATION REPORT";

        titleCell.font = {
            bold: true,
            size: 16,
            color: { argb: "774DEF" }
        };

        titleCell.alignment = {
            horizontal: "center",
            vertical: "middle"
        };

        rowIndex = 4;

        // ========================================
        // HELPERS
        // ========================================

        const addGap = (count = 1) => {

            for (let i = 0; i < count; i++) {
                sheet.addRow([]);
                rowIndex++;
            }
        };

        const addSectionTitle = (title) => {

            const row = sheet.addRow([title]);

            row.font = sectionFont;

            rowIndex++;

            return row;
        };

        const styleHeaderRow = (row) => {

            row.eachCell((cell) => {

                cell.fill = headerFill;

                cell.font = headerFont;

                cell.border = borderStyle;

                cell.alignment = centerAlignment;
            });
        };

        const styleDataRow = (row) => {

            row.eachCell((cell) => {

                cell.border = borderStyle;

                cell.alignment = {
                    vertical: "middle",
                    horizontal: "left"
                };
            });
        };

        // ========================================
        // DESCRIPTION
        // ========================================

        addSectionTitle("DESCRIPTION");

        let row = sheet.addRow([
            description || ""
        ]);

        styleDataRow(row);

        rowIndex++;

        addGap();

        // ========================================
        // CAL PARAMETERS
        // ========================================

        addSectionTitle("CAL PARAMETERS");

        let headerRow = sheet.addRow([
            "Parameter",
            "Value"
        ]);

        styleHeaderRow(headerRow);

        rowIndex++;

        Object.entries(result).forEach(([k, v]) => {

            const r = sheet.addRow([
                k,
                String(v)
            ]);

            styleDataRow(r);

            rowIndex++;
        });

        addGap();

        // ========================================
        // PREDICTIONS
        // ========================================

        addSectionTitle("PREDICTIONS");

        headerRow = sheet.addRow([
            "Sample ID",
            "Response",
            "Concentration"
        ]);

        styleHeaderRow(headerRow);

        rowIndex++;

        rows.forEach((r) => {

            const dataRow = sheet.addRow([
                r.sampleId,
                r.response,
                r.concentration
            ]);

            styleDataRow(dataRow);

            rowIndex++;
        });

        addGap();

        // ========================================
        // CALIBRATOR
        // ========================================

        addSectionTitle("CALIBRATOR");

        headerRow = sheet.addRow([
            "Concentration",
            "Response"
        ]);

        styleHeaderRow(headerRow);

        rowIndex++;

        calData.forEach((r) => {

            const responses = String(
                r.resp || ""
            ).split(",");

            responses.forEach((resp) => {

                const dataRow = sheet.addRow([
                    r.conc,
                    resp.trim()
                ]);

                styleDataRow(dataRow);

                rowIndex++;
            });
        });

        addGap(2);

        // ========================================
        // GRAPH SECTION
        // ========================================

        addSectionTitle("VALIDATION GRAPH");

        const canvas = await html2canvas(
            chartRef.current
        );

        const imageBase64 = canvas.toDataURL(
            "image/png"
        );

        const imageId = workbook.addImage({
            base64: imageBase64,
            extension: "png"
        });

        sheet.addImage(imageId, {
            tl: {
                col: 0,
                row: rowIndex + 1
            },
            ext: {
                width: 850,
                height: 420
            }
        });

        // ========================================
        // AUTO COLUMN WIDTH
        // ========================================

        sheet.columns.forEach((column) => {

            let maxLength = 15;

            column.eachCell?.(
                { includeEmpty: true },
                (cell) => {

                    const cellValue = cell.value
                        ? cell.value.toString()
                        : "";

                    maxLength = Math.max(
                        maxLength,
                        cellValue.length + 5
                    );
                }
            );

            column.width = maxLength;
        });

        // ========================================
        // ROW HEIGHT
        // ========================================

        sheet.eachRow((row) => {
            row.height = 22;
        });

        // ========================================
        // EXPORT
        // ========================================

        const buffer =
            await workbook.xlsx.writeBuffer();

        saveAs(
            new Blob([buffer]),
            "Prediction_Report.xlsx"
        );
    };
   

    const handleExportPdf = async () => {

        const doc = new jsPDF("p", "mm", "a4");

        let y = 15;

        // =========================
        // TITLE
        // =========================
        doc.setFontSize(14);
        doc.text("LIMS PORTAL VALIDATION REPORT", 14, y);

        y += 10;

        doc.setFontSize(11);
        doc.text(`Description: ${description || ""}`, 14, y);

        y += 10;

        // =========================
        // CAL PARAMETERS
        // =========================
        doc.setFontSize(12);
        doc.setFont("Verdana", "bold");
        doc.text("CAL PARAMETERS", 14, y);

        y += 6;

        autoTable(doc, {
            startY: y,
            head: [["Key", "Value"]],
            body: Object.entries(result).map(([k, v]) => [k, String(v)]),
            headStyles: {
                fillColor: [119, 77, 239],
                textColor: [255, 255, 255],
                fontStyle: "bold"
            }
        });

        y = doc.lastAutoTable.finalY + 10;

        // =========================
        // PREDICTIONS
        // =========================
        doc.setFontSize(12);
        doc.setFont("Verdana", "bold");
        doc.text("PREDICTIONS", 14, y);

        y += 6;

        autoTable(doc, {
            startY: y,
            head: [["Sample ID", "Response", "Concentration"]],
            body: rows.map(r => [
                r.sampleId,
                r.response,
                r.concentration
            ]),
            headStyles: {
                fillColor: [119, 77, 239],
                textColor: [255, 255, 255],
                fontStyle: "bold"
            }
        });

        y = doc.lastAutoTable.finalY + 10;

        // =========================
        // CALIBRATOR
        // =========================
        const calibratorRows = [];

        calData.forEach(r => {
            const responses = String(r.resp || "").split(",");
            responses.forEach(resp => {
                calibratorRows.push([r.conc, resp.trim()]);
            });
        });

        doc.setFontSize(12);
        doc.setFont("Verdana", "bold");
        doc.text("CALIBRATOR", 14, y);

        y += 6;

        autoTable(doc, {
            startY: y,
            head: [["Concentration", "Response"]],
            body: calibratorRows,
            headStyles: {
                fillColor: [119, 77, 239],
                textColor: [255, 255, 255],
                fontStyle: "bold"
            }
        });

        // =========================
        // GRAPH
        // =========================
        const canvas = await html2canvas(chartRef.current);
        const imgData = canvas.toDataURL("image/png");

        doc.addPage();

        doc.setFontSize(12);
        doc.text("LIMS Portal Validation Graph", 14, 15);

        doc.addImage(imgData, "PNG", 10, 25, 185, 100);

        doc.save("Prediction_Report.pdf");
    };

    // =========================
    // CHART OPTION
    // =========================

    const [chartOption, setChartOption] = useState({
        chart: {
            type: "line"
        },
        title: {
            text: ""
        },
        series: []
    });

    const [showChart, setShowChart] = useState(false);

    useEffect(() => {
        console.log("raw data", rawData);

        if (!calData || !calData.length) {
            return;
        }

        // =========================
        // CALIBRATOR DATA
        // =========================
        const calibratorPoints = [];

        calData.forEach((row) => {

            const concentration = Number(row.conc || 0);

            const responses = String(row.resp || "")
                .split(",")
                .map((r) => Number(r.trim()))
                .filter((r) => !isNaN(r));

            responses.forEach((respValue) => {

                calibratorPoints.push([
                    concentration,
                    respValue
                ]);

            });

        });
        console.log("calibratorPoints", calibratorPoints);
        // SORT
        calibratorPoints.sort((a, b) => a[0] - b[0]);

        // =========================
        // PREDICTION DATA
        // =========================
        const predictionLineData = rows
            .filter(
                (r) =>
                    r.concentration !== undefined &&
                    r.concentration !== null &&
                    String(r.concentration).trim() !== "" &&
                    r.response !== undefined &&
                    r.response !== null &&
                    String(r.response).trim() !== ""
            )
            .map((r) => ([
                Number(r.concentration),
                Number(r.response)
            ]))
            .filter(
                (r) =>
                    !isNaN(r[0]) &&
                    !isNaN(r[1])
            );

        predictionLineData.sort((a, b) => a[0] - b[0]);
        console.log("predictionLineData", predictionLineData);
        setChartOption({
            credits: {
                enabled: false
            },
            chart: {
                zoomType: "xy"
            },

            title: {
                text: ""
            },

            legend: {
                enabled: true,
                align: "center",
                verticalAlign: "top",
                layout: "horizontal"
            },

            xAxis: {

                title: {
                    text: "Concentration",
                    style: {
                        fontSize: "14px",
                        fontWeight: "bold"
                    }
                }
            },

            yAxis: {
                title: {
                    text: "Response",
                    style: {
                        fontSize: "14px",
                        fontWeight: "bold"
                    }
                }
            },

            tooltip: {

                shared: true,

                formatter: function () {

                    const points = this.points || [this.point];

                    const values = points
                        .map(
                            (p) => `Response:<b> ${p.y}</b>`
                        )
                        .join("<br/>");

                    return `                   
                    Concentration:<b> ${this.x}</b>
                    <br/>${values}  `;
                }
            },

            plotOptions: {

                series: {

                    turboThreshold: 0,

                    marker: {

                        enabled: true,

                        radius: 5,

                        states: {

                            hover: {

                                enabled: true,

                                radius: 8
                            }
                        }
                    },

                    states: {

                        inactive: {
                            opacity: 1
                        }
                    }
                },

                line: {

                    lineWidth: 3,

                    marker: {

                        enabled: true,

                        radius: 5
                    }
                }
            },

            series: [

                // =========================
                // CALIBRATION CURVE
                // =========================
                {
                    name: "Calibration Curve",

                    type: "line",

                    data: calibratorPoints.map((p, index) => ({
                        x: p[0],
                        y: p[1],
                        marker: {
                            enabled: true
                        },
                        custom: {
                            pointIndex: index + 1
                        }
                    })),

                    color: "#774def",

                    lineWidth: 3,

                    stickyTracking: false,

                    marker: {

                        enabled: true,

                        radius: 5,

                        symbol: "circle",

                        fillColor: "#774def",

                        lineColor: "#774def",

                        lineWidth: 2
                    },

                    tooltip: {

                        pointFormatter: function () {

                            return `
                <span style="color:${this.color}">●</span>
                Calibration Curve
                <br/>
                Point: <b>${this.custom.pointIndex}</b>
                <br/>
                Concentration: <b>${this.x}</b>
                <br/>
                Response: <b>${this.y}</b>
                <br/>
            `;
                        }
                    }
                },
                // =========================
                // CALIBRATION POINTS
                // =========================
                {
                    name: "Calibrator Points",

                    type: "scatter",

                    data: calibratorPoints,

                    color: "#ff1493",

                    marker: {
                        radius: 5,
                        symbol: "circle"
                    }
                },

                // =========================
                // PREDICTION LINE
                // =========================
                {
                    name: "Prediction Curve",

                    type: "line",

                    data: predictionLineData.map((p, index) => ({
                        x: p[0],
                        y: p[1],
                        custom: {
                            pointIndex: index + 1
                        }
                    })),

                    color: "#00a86b",

                    lineWidth: 3,

                    stickyTracking: false,

                    marker: {

                        enabled: true,

                        radius: 5,

                        symbol: "diamond",

                        fillColor: "#00a86b",

                        lineColor: "#00a86b",

                        lineWidth: 2
                    },

                    tooltip: {

                        pointFormatter: function () {

                            return `
                <span style="color:${this.color}">●</span>
                Prediction Line
                <br/>
                Point: <b>${this.custom.pointIndex}</b>
                <br/>
                Concentration: <b>${this.x}</b>
                <br/>
                Response: <b>${this.y}</b>
                <br/>
            `;
                        }
                    }
                },
                // =========================
                // PREDICTION POINTS
                // =========================
                {
                    name: "Prediction Points",

                    type: "scatter",

                    data: predictionLineData.map((p, index) => ({
                        x: p[0],
                        y: p[1],
                        custom: {
                            pointIndex: index + 1
                        }
                    })),

                    color: "#00a86b",

                    marker: {
                        radius: 5,
                        symbol: "diamond"
                    }
                },
            ]
        });

    }, [calData, rows]);

    // =========================
    // LOAD PREDICTION ROWS
    // =========================

    useEffect(() => {

        if (
            rawData?.predictionRows &&
            Array.isArray(rawData.predictionRows)
        ) {

            setRows(rawData.predictionRows);

            if (
                rawData.predictionRows.some(
                    (r) =>
                        r.concentration !== undefined &&
                        r.concentration !== null &&
                        String(r.concentration).trim() !== ""
                )
            ) {

                setShowChart(true);
            }

        }

    }, [rawData]);

    // =========================
    // REFRESH DETECTION
    // =========================
    useEffect(() => {

        const handleKeyDown = (e) => {

            // F5
            if (e.key === "F5") {

                e.preventDefault();

                setPendingRefresh(true);

                setOpenResetDialog(true);
            }

            // CTRL + R
            if (
                (e.ctrlKey || e.metaKey) &&
                e.key.toLowerCase() === "r"
            ) {

                e.preventDefault();

                setPendingRefresh(true);

                setOpenResetDialog(true);
            }
        };

        window.addEventListener(
            "keydown",
            handleKeyDown
        );

        return () => {

            window.removeEventListener(
                "keydown",
                handleKeyDown
            );
        };

    }, []);
    // =========================
    // KEEP SELECTED RUN
    // =========================
    const handleKeepRun = () => {

        setOpenResetDialog(false);

        if (pendingRefresh) {

            window.location.reload();
        }
    };

    // =========================
    // CLEAR SELECTED RUN
    // =========================
    const handleClearRun = () => {

        sessionStorage.removeItem(
            "selectedRunId"
        );

        setRows(createEmptyRows(5));

        setShowChart(false);

        setInputValue("");

        setOpenResetDialog(false);

        if (pendingRefresh) {

            window.location.reload();
        }
    };
    const isResetDisabled = useMemo(() => {
        const rowsData = rows || rawData.predictionRows || [];

        // Enable if more than default rows
        if (rowsData.length > 5) {
            return false;
        }

        // Enable if any row has data
        const hasData = rowsData.some((r) => {
            const response =
                r.response?.toString().trim() || "";

            const concentration =
                r.concentration?.toString().trim() || "";

            return (
                response !== "" ||
                concentration !== ""
            );
        });

        // Disable only for default empty state
        return !hasData;
    }, [rows, rawData.predictionRows]);
    const hasCalibrationResponse =
        calibrationResponse &&
        typeof calibrationResponse === "object" &&
        Object.keys(calibrationResponse).length > 0;

    if (!runId || !hasCalibrationResponse) {
        return (
            <Box p={3}>
                <Alert severity="error">
                    Calibration data is unavailable. Please select an existing calibration run or perform a new calibration to continue..
                </Alert>
            </Box>
        );
    }

    const displayCalibrationEntries = Object.entries(
        calibrationResponse || {}
    ).filter(([key]) => key !== "eWetCalModelType");

    return (

        <Box
            sx={{
                p: {
                    xs: 1.5,
                    sm: 2,
                    md: 3
                },
                width: "100%",
                overflowX: "hidden"
            }}
        >


            <Box
                sx={{
                    display: "flex",
                    flexDirection: {
                        xs: "column",
                        lg: "row"
                    },
                    justifyContent: "space-between",
                    alignItems: {
                        xs: "stretch",
                        lg: "flex-start"
                    },
                    gap: 2,
                    width: "100%"
                }}
            >
                {/* LEFT SECTION */}
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0
                    }}
                >
                    <Typography
                        variant="h6"
                        sx={{
                            wordBreak: "break-word",
                            fontSize: {
                                xs: "1rem",
                                sm: "1.1rem",
                                md: "1.25rem"
                            }
                        }}
                    >
                        Description : {description}
                    </Typography>
                </Box>

                {/* RIGHT SECTION */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        width: {
                            xs: "100%",
                            lg: "auto"
                        }
                    }}
                >
                    {/* EXPORT BUTTONS */}
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1.5,
                            justifyContent: {
                                xs: "stretch",
                                sm: "flex-start",
                                lg: "flex-end"
                            }
                        }}
                    >
                        <Button
                            fullWidth={window.innerWidth < 600}
                            variant="contained"
                            color="primary"
                            startIcon={<PictureAsPdfIcon />}
                            onClick={handleExportPdf}
                            disabled={!showChart}
                        >
                            Export PDF
                        </Button>

                        <Button
                            fullWidth={window.innerWidth < 600}
                            variant="contained"
                            color="success"
                            startIcon={<GridOnIcon />}
                            onClick={handleExportExcel}
                            disabled={!showChart}
                        >
                            Export Excel
                        </Button>
                    </Box>


                </Box>
            </Box>
            {/* TABLES */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: {
                        xs: "column",
                        xl: "row",
                    },
                    gap: 2,
                    width: "100%",
                    alignItems: "stretch",
                }}
            >
                {/* CALIBRATION INPUT DATA */}
                {calData?.length > 0 && (
                    <Paper
                        elevation={2}
                        sx={{
                            p: 2,
                            borderRadius: 2,
                            width: "100%",
                            flex: 1,
                            minWidth: 0,
                            minHeight: {
                                xs: 160,
                                sm: 200,
                                md: 220,
                            },
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                        }}
                    >
                        <Typography
                            variant="h6"
                            gutterBottom
                            fontWeight={400}
                            sx={{
                                fontSize: {
                                    xs: "1rem",
                                    sm: "1.1rem",
                                },
                                flexShrink: 0,
                            }}
                        >
                            Calibrator Points
                        </Typography>

                        <Box
                            sx={{
                                flex: 1,
                                overflow: "auto", 
                                border: "1px solid #eee",
                                borderRadius: 1,
                                minHeight: 0,

                                /* visible desktop scrollbar */
                                "&::-webkit-scrollbar": {
                                    width: "10px",
                                    height: "10px",
                                },

                                "&::-webkit-scrollbar-track": {
                                    background: "#f1f1f1",
                                    borderRadius: "10px",
                                },

                                "&::-webkit-scrollbar-thumb": {
                                    background: "#bdbdbd",
                                    borderRadius: "10px",
                                },

                                "&::-webkit-scrollbar-thumb:hover": {
                                    background: "#9e9e9e",
                                },

                                scrollbarWidth: "thin",
                            }}
                        >
                            <Table
                                stickyHeader
                                size="small"
                                sx={{
                                    minWidth: 420,
                                    tableLayout: "auto",
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            sx={{
                                                backgroundColor:
                                                    "#774def",
                                                color: "#fff",
                                                fontWeight: 500,
                                                whiteSpace:
                                                    "nowrap",
                                                position: "sticky",
                                                top: 0,
                                                zIndex: 2,
                                            }}
                                        >
                                            Concentration
                                        </TableCell>

                                        <TableCell
                                            sx={{
                                                backgroundColor:
                                                    "#774def",
                                                color: "#fff",
                                                fontWeight: 500,
                                                whiteSpace:
                                                    "nowrap",
                                                position: "sticky",
                                                top: 0,
                                                zIndex: 2,
                                            }}
                                        >
                                            Response
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {calData.map((row, index) => (
                                        <TableRow key={index}>
                                            <TableCell
                                                sx={{
                                                    whiteSpace:
                                                        "nowrap",
                                                }}
                                            >
                                                {
                                                    row.conc
                                               }
                                            </TableCell>

                                            <TableCell
                                                sx={{
                                                    whiteSpace:
                                                        "nowrap",
                                                }}
                                            >
                                                {
                                                    row.resp }
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Paper>
                )}

                {/* CALIBRATION RESPONSE */}
                {calibrationResponse &&
                    Object.keys(calibrationResponse).length >
                    0 && (
                        <Paper
                            elevation={2}
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                width: "100%",
                                flex: 1,
                                minWidth: 0,
                                minHeight: {
                                    xs: 160,
                                    sm: 200,
                                    md: 220,
                                },
                                display: "flex",
                                flexDirection: "column",
                                overflow: "hidden",
                            }}
                        >
                            <Typography
                                variant="h6"
                                gutterBottom
                                fontWeight={400}
                                sx={{
                                    fontSize: {
                                        xs: "1rem",
                                        sm: "1.1rem",
                                    },
                                    flexShrink: 0,
                                }}
                            >
                                Cal Parameters
                            </Typography>

                            <Box
                                sx={{
                                    flex: 1,
                                    overflow: "auto", 
                                    border: "1px solid #eee",
                                    borderRadius: 1,
                                    minHeight: 0,

                                    "&::-webkit-scrollbar": {
                                        width: "10px",
                                        height: "10px",
                                    },

                                    "&::-webkit-scrollbar-track":
                                    {
                                        background:
                                            "#f1f1f1",
                                        borderRadius:
                                            "10px",
                                    },

                                    "&::-webkit-scrollbar-thumb":
                                    {
                                        background:
                                            "#bdbdbd",
                                        borderRadius:
                                            "10px",
                                    },

                                    "&::-webkit-scrollbar-thumb:hover":
                                    {
                                        background:
                                            "#9e9e9e",
                                    },

                                    scrollbarWidth: "thin",
                                }}
                            >
                                <Table
                                    stickyHeader
                                    size="small"
                                    sx={{
                                        minWidth: 700,
                                        tableLayout: "auto",
                                    }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            {Object.keys(
                                                calibrationResponse || {}
                                            ).map((key) => (
                                                <TableCell
                                                    key={key}
                                                    sx={{
                                                        backgroundColor:
                                                            "#774def",
                                                        color: "#fff",
                                                        fontWeight: 500,
                                                        whiteSpace:
                                                            "nowrap",
                                                        position:
                                                            "sticky",
                                                        top: 0,
                                                        zIndex: 2,
                                                    }}
                                                >
                                                    {key}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>

                                    <TableBody>
                                        <TableRow>
                                            {Object.values(
                                                calibrationResponse || {}
                                            ).map(
                                                (
                                                    value,
                                                    index
                                                ) => (
                                                    <TableCell
                                                        key={index}
                                                        sx={{
                                                            whiteSpace:
                                                                "nowrap",
                                                        }}
                                                    >
                                                        {typeof value ===
                                                            "number"
                                                            ? value.toFixed(
                                                                6
                                                            )
                                                            : String(
                                                                value
                                                            )}
                                                    </TableCell>
                                                )
                                            )}
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </Box>
                        </Paper>
                    )}
            </Box>

            <Typography
                mt={2}
                variant="h5"
                gutterBottom
                sx={{
                    fontSize: {
                        xs: "1.25rem",
                        sm: "1.5rem"
                    }
                }}
            >
                Prediction Tool
            </Typography>

            <Grid
                container
                spacing={{
                    xs: 2,
                    md: 2.5
                }}
                alignItems="stretch"
            >

                {/* INPUT */}
                <Grid
                    item
                    xs={12}
                    lg={4}
                    sx={{
                        display: "flex",
                        minWidth: 0
                    }}
                >

                    <Paper
                        sx={{
                            p: 0.5,
                            pb: 1,
                            width: "100%",
                            height: {
                                xs: "auto",
                                lg: "50vh"
                            },

                            minHeight: {
                                xs: 300,
                                sm: 340,
                                md: 360
                            },                           

                            display: "flex",
                            flexDirection: "column",

                            overflow: "hidden"
                        }}
                    >

                        <Typography>
                            Predictions Data
                        </Typography>

                        {/* DRAG DROP AREA */}
                        <Box
                            onDrop={handleDrop}
                            onDragOver={(e) =>
                                e.preventDefault()
                            }
                            sx={{
                                position: "relative",

                                flex: 1,

                                minHeight: 0,

                                display: "flex",
                                flexDirection: "column",

                                mt: 1,
                                mb: 1,

                                overflow: "hidden"
                            }}
                        >

                            {/* TEXTAREA */}
                            <Box
                                sx={{
                                    width: "100%",

                                    flex: 1,

                                    minHeight: 0,

                                    overflow: "hidden",

                                    border: "1px solid #ccc",
                                    borderRadius: 1,

                                    p: 1,

                                    bgcolor: "#fff",

                                    display: "flex",
                                    flexDirection: "column"
                                }}
                            >
                                <TextField
                                    multiline
                                    fullWidth
                                    variant="standard"
                                    value={inputValue}
                                    onChange={handleBulkInput}
                                    placeholder={`Example:
Sample ID          Response
1                  -0.015582
2                  -0.0172285`}
                                    InputProps={{
                                        disableUnderline: true
                                    }}
                                    inputProps={{
                                        style: {
                                            fontFamily: "monospace",
                                            whiteSpace: "pre",
                                            overflowWrap: "normal"
                                        }
                                    }}
                                    sx={{
                                        width: "100%",

                                        flex: 1,

                                        minHeight: 0,

                                        display: "flex",
                                        flexDirection: "column",

                                        "& .MuiInputBase-root": {
                                            fontFamily: "monospace",

                                            alignItems: "flex-start",

                                            flex: 1,

                                            minHeight: 0,

                                            overflow: "hidden"
                                        },

                                        "& .MuiInputBase-inputMultiline": {
                                            overflow: "hidden"
                                        },

                                        "& textarea": {
                                            fontFamily: "monospace !important",

                                            whiteSpace: "pre !important",

                                            overflowX: "auto !important",
                                            overflowY: "auto !important",

                                            resize: "none",

                                            flex: 1,

                                            minHeight: {
                                                xs: "260px !important",
                                                sm: "300px !important"
                                            },

                                            height: "100% !important",

                                            boxSizing: "border-box",

                                            paddingRight: "8px",

                                            fontSize: {
                                                xs: "12px",
                                                sm: "13px",
                                                md: "14px"
                                            }
                                        }
                                    }}

                                />
                            </Box>
                            {/* OVERLAY BUTTON */}
                            {!inputValue && (

                                <Box
                                    sx={{
                                        position: "absolute",
                                        top: "50%",
                                        left: "50%",
                                        transform: "translate(-50%, -50%)",

                                        zIndex: 2,

                                        width: "90%",

                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",

                                        textAlign: "center",

                                        gap: 1,

                                        pointerEvents: "none"
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                    >
                                        Drag & drop, paste or enter values.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={
                                            <UploadFileIcon />
                                        }
                                        sx={{
                                            backgroundColor:
                                                "#774def",

                                            pointerEvents:
                                                "auto"
                                        }}
                                        onClick={() => {

                                            if (fileInputRef.current) {
                                                fileInputRef.current.value = null;
                                                fileInputRef.current.click();
                                            }

                                        }}
                                    >
                                        Upload File
                                    </Button>



                                </Box>

                            )}

                            {/* HIDDEN FILE INPUT */}
                            <input
                                ref={fileInputRef}
                                type="file"
                                hidden
                                accept=".txt,.csv,.xlsx,.xls"
                                onChange={handleFileUpload}
                            />

                        </Box>
                        {error && (

                            <Alert
                                severity="error"
                                sx={{ mt: 2 }}
                            >
                                {error}
                            </Alert>

                        )}

                        <Box
                            sx={{
                                display: "flex",

                                justifyContent: {
                                    xs: "stretch",
                                    sm: "flex-end"
                                },

                                flexDirection: {
                                    xs: "column",
                                    sm: "row"
                                },

                                gap: 1,

                                width: "100%",

                                mt: "auto",

                                pt: 1,

                                flexShrink: 0
                            }}
                        >

                            <Button
                                fullWidth={window.innerWidth < 600}
                                variant="outlined"
                                color="secondary"
                                onClick={handleClear}
                                startIcon={<ClearIcon />}
                                disabled={!inputValue}
                            >
                                Clear Values
                            </Button>

                        </Box>

                    </Paper>

                </Grid>

                {/* TABLE */}
                <Grid
                    item
                    xs={12}
                    lg={8}
                    sx={{
                        display: "flex",
                        minWidth: 0
                    }}
                >

                    <Paper
                        sx={{
                            p: 0.5,
                            pb: 1,
                            width: "100%",
                            height: {
                                xs: "auto",
                                lg: "50vh"
                            },

                            minHeight: {
                                xs: 300,
                                sm: 340,
                                md: 360
                            },                            
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden"
                        }}
                    >

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: {
                                    xs: "column",
                                    sm: "row"
                                },
                                justifyContent: "space-between",
                                alignItems: {
                                    xs: "stretch",
                                    sm: "center"
                                },
                                gap: 1.5
                            }}
                        >

                            <Typography>
                                Predictions
                            </Typography>

                            <Box
                                display="flex"
                                justifyContent="flex-end"
                                gap={1}
                                width="100%"
                                flexWrap="wrap"
                            >
                                <Button
                                    variant="contained"
                                    sx={{
                                        backgroundColor:
                                            "#774def"
                                    }}
                                    onClick={handlePredict}
                                    disabled={
                                        predictLoading ||
                                        !rows.some(
                                            (r) =>
                                                r.response !== undefined &&
                                                r.response !== null &&
                                                String(r.response).trim() !== ""
                                        )
                                    }
                                    startIcon={<AutoAwesomeIcon />}
                                >

                                    {predictLoading
                                        ? "Predicting..."
                                        : "Run Prediction"}

                                </Button>


                            </Box>

                        </Box>

                        {predictError && (

                            <Alert
                                severity="error"
                                sx={{ mt: 2 }}
                            >
                                {predictError}
                            </Alert>

                        )}
                        <Divider sx={{ my: 1 }} />

                        <Box
                            sx={{
                                flex: 1,
                                overflowY: "auto",
                                pr: 0,
                                mb: 1
                            }}
                        >

                            {rows.map((row, i) => (

                                <Box
                                    key={i}
                                    sx={{
                                        display: "flex",
                                        flexDirection: {
                                            xs: "column",
                                            md: "row"
                                        },
                                        gap: 1,
                                        mb: 1,
                                        mt: 1,
                                        alignItems: {
                                            xs: "stretch",
                                            md: "center"
                                        },
                                        width: "100%"
                                    }}
                                >

                                    <TextField
                                        size="small"
                                        label="Sample ID"
                                        value={
                                            row.sampleId
                                        }
                                        onChange={(e) =>
                                            handleRowChange(
                                                i,
                                                "sampleId",
                                                e.target
                                                    .value
                                            )
                                        }
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            width: {
                                                xs: "100%",
                                                md: "auto"
                                            }
                                        }}
                                    />

                                    <TextField
                                        size="small"
                                        label="Response"
                                        value={
                                            row.response
                                        }
                                        onChange={(e) =>
                                            handleRowChange(
                                                i,
                                                "response",
                                                e.target
                                                    .value
                                            )
                                        }
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            width: {
                                                xs: "100%",
                                                md: "auto"
                                            }
                                        }}
                                    />

                                    <TextField
                                        size="small"
                                        label="Concentration"
                                        value={
                                            row.concentration ||
                                            ""
                                        }
                                        InputProps={{
                                            readOnly: true
                                        }}
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            width: {
                                                xs: "100%",
                                                md: "auto"
                                            }
                                        }}
                                    />
                                    <IconButton
                                        sx={{
                                            alignSelf: {
                                                xs: "flex-end",
                                                md: "center"
                                            }
                                        }} onClick={() =>
                                            handleDeleteRow(
                                                i
                                            )
                                        } color="error">
                                        <DeleteIcon />
                                    </IconButton>

                                </Box>

                            ))}

                        </Box>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: {
                                    xs: "stretch",
                                    sm: "flex-end"
                                },
                                flexDirection: {
                                    xs: "column",
                                    sm: "row"
                                },
                                gap: 1,
                                width: "100%",
                                mt: 1
                            }}
                        >
                            <Button
                                size="small"
                                variant="contained"
                                sx={{
                                    backgroundColor:
                                        "#774def"
                                }}
                                onClick={
                                    handleAddRow
                                }
                                startIcon={<AddIcon />}
                            >
                                Add Predict
                            </Button>

                            <Button
                                size="small"
                                variant="outlined"
                                color="secondary"
                                startIcon={<RestartAltIcon />}
                                onClick={
                                    handleResetRows
                                }
                                disabled={isResetDisabled}
                            >
                                Reset Predict
                            </Button>
                        </Box>
                    </Paper>

                </Grid>


                {/* GRAPH */}
                {showChart && (

                    <Grid item xs={12}>

                        <Paper
                            sx={{
                                p: {
                                    xs: 1,
                                    sm: 2
                                },
                                overflowX: "auto"
                            }}
                        >

                            <Typography variant="h6">
                                LIMS Portal Validation
                            </Typography>

                            <div ref={chartRef}>
                                <HighchartsReact
                                    highcharts={Highcharts}
                                    options={{
                                        ...chartOption,
                                        responsive: {
                                            rules: [
                                                {
                                                    condition: {
                                                        maxWidth: 768
                                                    },
                                                    chartOptions: {
                                                        legend: {
                                                            layout: "horizontal",
                                                            align: "center",
                                                            verticalAlign: "bottom"
                                                        },
                                                        xAxis: {
                                                            labels: {
                                                                style: {
                                                                    fontSize: "10px"
                                                                }
                                                            }
                                                        },
                                                        yAxis: {
                                                            labels: {
                                                                style: {
                                                                    fontSize: "10px"
                                                                }
                                                            }
                                                        }
                                                    }
                                                }
                                            ]
                                        }
                                    }}
                                    containerProps={{
                                        style: {
                                            width: "100%",
                                            overflowX: "auto"
                                        }
                                    }}
                                />
                            </div>

                        </Paper>

                    </Grid>

                )}

            </Grid>
            <ConfirmDialog
                open={openResetDialog}
                title="Maintain Prediction Run"
                message="Do you want to maintain the selected run details after refresh?"
                onCancel={handleClearRun}
                onConfirm={handleKeepRun}
                confirmText="Yes"
                cancelText="No"
            />
            <ConfirmDialog
                open={openValidationDialog}
                title="Validation Error"
                message={validationMessage}
                onCancel={() => setOpenValidationDialog(false)}
                onConfirm={() => setOpenValidationDialog(false)}
            />
        </Box>
    );
};

export default Predict;
